/**
 * RQB Gift Direct Block — Custom web component
 * Simplified version of rqb-gift-block that directly displays the
 * first available gift product without a modal/selection flow.
 * Adds _rqb_* properties for the RQB discount function on form submit.
 */
(() => {
  if (customElements.get('rqb-gift-direct')) return;

  function getVisitorId() {
    var id = null;
    try { id = localStorage.getItem('_rqb_visitor_id'); } catch (e) {}
    if (id) return id;
    try {
      var m = document.cookie.split('; ').find(function (c) { return c.indexOf('_rqb_vid=') === 0; });
      if (m) id = m.split('=')[1];
    } catch (e) {}
    if (!id) id = (crypto && crypto.randomUUID ? crypto.randomUUID() : Date.now() + '-' + Math.random().toString(36).slice(2));
    try { localStorage.setItem('_rqb_visitor_id', id); } catch (e) {}
    try { document.cookie = '_rqb_vid=' + id + '; path=/; max-age=63072000; SameSite=Lax'; } catch (e) {}
    return id;
  }

  class RqbGiftDirect extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      // Parse offer data
      const raw = this.querySelector('script[data-rqb-offer]');
      this.offerData = raw ? JSON.parse(raw.textContent) : null;

      // Read pre-selected product from server-rendered card
      const card = this.querySelector('[data-rqb-direct-card]');
      if (card) {
        this.selectedData = {
          productId: card.dataset.productId || '',
          variantId: card.dataset.variantId || '',
        };
      }

      // Intercept the product form submission
      const formId = this.dataset.productFormId;
      if (formId) {
        this.productForm = document.getElementById(formId);
        if (this.productForm) {
          this.boundHandleFormSubmit = this.handleFormSubmit.bind(this);
          this.productForm.addEventListener('submit', this.boundHandleFormSubmit, { capture: true });
        }
      }
    }

    disconnectedCallback() {
      if (this.productForm && this.boundHandleFormSubmit) {
        this.productForm.removeEventListener('submit', this.boundHandleFormSubmit, { capture: true });
      }
    }

    /* ---- Cart: intercept form submit with RQB properties ---- */

    handleFormSubmit(event) {
      if (document.body.getAttribute('free-gift-single-offer-ab-test-version-1') !== 'true') return;
      if (!this.selectedData?.variantId) return;

      const form = this.productForm;
      if (!form) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      const submitButton = form.querySelector('[type="submit"]');
      if (submitButton?.hasAttribute('aria-disabled')) return;

      // Clear errors
      const errMsg = form.querySelector('.product-form__error-message');
      if (errMsg) { errMsg.toggleAttribute('hidden', true); errMsg.innerText = ''; }

      // Read form data
      const formData = new FormData(form);
      const mainVariantId = parseInt(formData.get('id'));
      const quantity = parseInt(formData.get('quantity')) || 1;

      // Collect main product properties
      const mainProperties = {};
      for (const [key, value] of formData.entries()) {
        const match = key.match(/^properties\[(.+)\]$/);
        if (match && value !== '') mainProperties[match[1]] = value;
      }

      // Build RQB metadata
      const offer = this.offerData?.offer;
      const bar = offer?.bars?.find(b => b.enabled !== false) || offer?.bars?.[0];
      const bundleId = 'rqb_' + (offer?.offerId || 'custom') + '_' + Date.now() + Math.random().toString(36).slice(2);
      const visitorId = getVisitorId();

      // RQB properties for the main item
      if (offer?.offerId) {
        mainProperties._rqb_bundle_id = bundleId;
        mainProperties._rqb_role = 'main';
        mainProperties._rqb_bar_position = String(bar?.position ?? 0);
        mainProperties._rqb_bar_id = bar?.id || '';
        mainProperties._rqb_offer_id = offer.offerId;
        mainProperties._rqb_visitor_id = visitorId;
      }

      // Gift item properties
      const giftProps = { '_is_free_gift': 'true' };
      if (offer?.offerId) {
        giftProps._rqb_bundle_id = bundleId;
        giftProps._rqb_role = 'gift';
        giftProps._rqb_offer_id = offer.offerId;
        giftProps._rqb_visitor_id = visitorId;
        giftProps._rqb_line_uid = Date.now() + '_' + Math.random().toString(36).slice(2);
      }

      // Build items
      const items = [
        {
          id: mainVariantId,
          quantity: quantity,
          properties: mainProperties,
        },
        {
          id: parseInt(this.selectedData.variantId),
          quantity: 1,
          parent_id: mainVariantId,
          properties: giftProps,
        },
      ];

      // Gather sections for cart drawer
      let sectionsToBundle = [];
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:grouped-sections', {
          bubbles: true,
          detail: { sections: sectionsToBundle },
        })
      );

      // Show loading
      submitButton.setAttribute('aria-disabled', 'true');
      submitButton.classList.add('btn--loading');

      const lastSubmittedElement = event.submitter || event.currentTarget;
      const isPageCart = document.body.classList.contains('cart-template') || FoxTheme.settings.cartType === 'page';

      const body = JSON.stringify({
        items,
        sections: sectionsToBundle,
        sections_url: window.location.pathname,
      });

      fetch(FoxTheme.routes.cart_add_url, {
        ...FoxTheme.utils.fetchConfig(),
        body,
      })
        .then((r) => r.json())
        .then(async (parsedState) => {
          if (parsedState.status) {
            FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartError, {
              source: 'product-form',
              productVariantId: mainVariantId.toString(),
              errors: parsedState.errors || parsedState.description,
              message: parsedState.message,
            });
            if (errMsg) { errMsg.toggleAttribute('hidden', false); errMsg.innerText = parsedState.description; }
            else if (form.dataset.hideErrors !== 'true') alert(parsedState.description);
            document.dispatchEvent(new CustomEvent('product-ajax:error', { detail: { errorMessage: parsedState.description } }));
            return;
          }

          const cartJson = await (await fetch(FoxTheme.routes.cart_url, { ...FoxTheme.utils.fetchConfig() })).json();
          cartJson.sections = parsedState.sections;

          FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cartJson });
          document.dispatchEvent(new CustomEvent('product-ajax:added', { detail: { product: parsedState.items?.[0] || parsedState } }));

          if (isPageCart) { window.location.href = FoxTheme.routes.cart_url; return; }

          const cartDrawer = document.querySelector('cart-drawer');
          const upsellWidget = document.querySelector('[js-product-upsell-widget]');
          const isAbTestV1 = document.body.getAttribute('main-product-block-ab-testing-version-1') === 'true';
          const quickViewModal = form.closest('quick-view-modal');

          const openDrawer = () => {
            if (quickViewModal) {
              if (cartDrawer && !cartDrawer.open) {
                document.body.addEventListener(quickViewModal.events.handleAfterHide, () => {
                  setTimeout(() => cartDrawer.show(lastSubmittedElement));
                }, { once: true });
              }
              quickViewModal.hide(true);
            } else {
              cartDrawer && cartDrawer.show(lastSubmittedElement);
            }
          };

          if (!upsellWidget || upsellWidget.classList.contains('hide-product-upsell') || isAbTestV1) {
            openDrawer();
          } else {
            upsellWidget.classList.add('active');
          }

          if (form.closest('[js-product-upsell-widget]')) {
            form.closest('[js-product-upsell-widget]').classList.remove('active');
            openDrawer();
          }
        })
        .catch((error) => {
          console.error('RQB gift direct add-to-cart error:', error);
        })
        .finally(() => {
          submitButton.classList.remove('btn--loading');
          submitButton.removeAttribute('aria-disabled');
        });
    }
  }

  customElements.define('rqb-gift-direct', RqbGiftDirect);
})();
